import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Security, LoginCallback } from '@okta/okta-react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import oktaAuth from './config/okta';
import { Login, ProtectedRoute } from './components/Auth';
import { AppLayout } from './components/Layout';
import { TenantProvider } from './contexts/TenantProvider';
import { AiUsageProvider } from './contexts/AiUsageProvider';
import { ProjectProgressProvider } from './contexts/ProjectProgressContext';
import { CustomAlertProvider, ErrorToastProvider } from './components/common';
import SaveProgressDialog from './components/common/SaveProgressDialog';
import NavigationBlocker from './components/common/NavigationBlocker';
import { useProjectProgress } from './contexts/ProjectProgressContext';
import { UsersPage } from './pages/Users';

import LandingPage from './pages/Landing/LandingPage';
import PreviewPage from './pages/Preview/PreviewPage';
import FiltersPage from './pages/Filters/FiltersPage';
import CardsPage from './pages/Cards/CardsPage';
import DashboardPage from './pages/Dashboard/DashboardPage';
import DashboardListPage from './pages/Dashboard/DashboardListPage';
import TenantsPage from './pages/Tenants/TenantsPage';
import SearchPage from './pages/Search/SearchPage';
import FavoritesPage from './pages/Favorites/FavoritesPage';
import ProjectsPage from './pages/Projects/ProjectsPage';
import GroupsPage from './pages/Groups/GroupsPage';
import SettingsPage from './pages/Settings/SettingsPage';
import ClientsPage from './pages/Clients/ClientsPage';
import AiModelsPage from './pages/AiModels/AiModelsPage';
import AiUsageOverviewPage from './pages/AiModels/AiUsageOverviewPage';
import HomeEntry from './components/Auth/HomeEntry';
import { DASHBOARD_ROUTE_PERMISSIONS } from './constants/dashboardAccessPermissions';
import { RolesPage } from './pages/Roles';
import { FONT_FAMILY_PRIMARY } from './constants/fonts';
import SurveyModuleLayout from './surveys/SurveyModuleLayout';
import SurveyManagePage from './surveys/pages/SurveyManagePage';
import SurveyCreatePage from './surveys/pages/SurveyCreatePage';
import SurveyBuilderPage from './surveys/pages/SurveyBuilderPage';
import SurveyPreviewPage from './surveys/pages/SurveyPreviewPage';
import SurveyResponsesPage from './surveys/pages/SurveyResponsesPage';
import SurveyResponsesHubPage from './surveys/pages/SurveyResponsesHubPage';
import SurveyPublicPage from './surveys/pages/SurveyPublicPage';
import SurveyPermissionRoute from './surveys/components/SurveyPermissionRoute';
import {
  SURVEY_MODULE_PERMISSIONS,
  SURVEY_MANAGE_ROUTE_PERMISSIONS,
  SURVEY_RESPONSES_ROUTE_PERMISSIONS,
} from './constants/surveyAccessPermissions';

const DASHBOARD_LIST_ROUTE_PERMISSIONS = [
  ...DASHBOARD_ROUTE_PERMISSIONS,
  'view_shared_dashboards',
];

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#000000',
    },
    secondary: {
      main: '#09ad40',
    },
  },
  typography: {
    fontFamily: FONT_FAMILY_PRIMARY,
    fontSize: 14,
    htmlFontSize: 16,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontFamily: FONT_FAMILY_PRIMARY,
        },
        body: {
          fontFamily: FONT_FAMILY_PRIMARY,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY_PRIMARY,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY_PRIMARY,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY_PRIMARY,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY_PRIMARY,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY_PRIMARY,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY_PRIMARY,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: FONT_FAMILY_PRIMARY,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY_PRIMARY,
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          fontFamily: FONT_FAMILY_PRIMARY,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        message: {
          fontFamily: FONT_FAMILY_PRIMARY,
        },
      },
    },
  },
});

const AppContentWithDialog = () => {
  const { showSaveDialog, saving, handleSaveConfirm, handleSaveCancel, handleSaveDismiss, currentProject, changesTracker } = useProjectProgress();
  
  return (
    <>
      <NavigationBlocker />
      <Routes>
          <Route path="/login" element={<LoginCallback />} />
          <Route path="/signin" element={<Login />} /> 
          <Route path="/login/callback" element={<LoginCallback />} />

          {/* Public respondent survey — no auth; minimal shell (no sidebar / tenant) */}
          <Route path="/survey/p/s/:token" element={<SurveyPublicPage />} />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <HomeEntry />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/favorites"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <FavoritesPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects"
            element={
              <ProtectedRoute requiredPermission="create_dashboard">
                <AppLayout>
                  <ProjectsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Question groups (survey templates): same access as managing surveys. */}
          <Route
            path="/groups"
            element={
              <ProtectedRoute requiredPermission={SURVEY_MANAGE_ROUTE_PERMISSIONS}>
                <AppLayout>
                  <GroupsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <SettingsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/landing"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <LandingPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/preview/:jobId"
            element={
              <ProtectedRoute requiredPermission="create_dashboard">
                <AppLayout>
                  <PreviewPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/filters/:jobId"
            element={
              <ProtectedRoute requiredPermission="create_dashboard">
                <AppLayout>
                  <FiltersPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/cards/:jobId"
            element={
              <ProtectedRoute requiredPermission="create_dashboard">
                <AppLayout>
                  <CardsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboards"
            element={
              <ProtectedRoute requiredPermission={DASHBOARD_LIST_ROUTE_PERMISSIONS}>
                <AppLayout>
                  <DashboardListPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute requiredPermission={DASHBOARD_ROUTE_PERMISSIONS}>
                <AppLayout>
                  <DashboardPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/:projectId"
            element={
              <ProtectedRoute requiredPermission={DASHBOARD_ROUTE_PERMISSIONS}>
                <AppLayout>
                  <DashboardPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/users/*"
            element={
              <ProtectedRoute requiredPermission="manage_users">
                <AppLayout>
                  <UsersPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/tenants/*"
            element={
              <ProtectedRoute requireJLLUser={true}>
                <AppLayout>
                  <TenantsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/clients"
            element={
              <ProtectedRoute requireJLLUser={true}>
                <AppLayout>
                  <ClientsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/roles"
            element={
              <ProtectedRoute requireJLLUser={true}>
                <AppLayout>
                  <RolesPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* AI model registry and pricing — drives what clients are billed for overage */}
          <Route
            path="/ai-models"
            element={
              <ProtectedRoute requireJLLUser={true}>
                <AppLayout>
                  <AiModelsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Cross-client AI token usage for the JLL master tenant */}
          <Route
            path="/ai-usage"
            element={
              <ProtectedRoute requireJLLUser={true}>
                <AppLayout>
                  <AiUsageOverviewPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/survey"
            element={
              <ProtectedRoute requiredPermission={SURVEY_MODULE_PERMISSIONS}>
                <AppLayout>
                  <SurveyModuleLayout />
                </AppLayout>
              </ProtectedRoute>
            }
          >
            <Route index element={<SurveyManagePage />} />
            <Route
              path="manage"
              element={
                <SurveyPermissionRoute permissions={SURVEY_MANAGE_ROUTE_PERMISSIONS}>
                  <SurveyManagePage />
                </SurveyPermissionRoute>
              }
            />
            <Route
              path="responses"
              element={
                <SurveyPermissionRoute permissions={SURVEY_RESPONSES_ROUTE_PERMISSIONS}>
                  <SurveyResponsesHubPage />
                </SurveyPermissionRoute>
              }
            />
            <Route
              path="new"
              element={
                <SurveyPermissionRoute permissions={SURVEY_MANAGE_ROUTE_PERMISSIONS}>
                  <SurveyCreatePage />
                </SurveyPermissionRoute>
              }
            />
            <Route
              path=":id/edit"
              element={
                <SurveyPermissionRoute permissions={SURVEY_MANAGE_ROUTE_PERMISSIONS}>
                  <SurveyBuilderPage />
                </SurveyPermissionRoute>
              }
            />
            <Route
              path=":id/preview"
              element={
                <SurveyPermissionRoute permissions={SURVEY_MANAGE_ROUTE_PERMISSIONS}>
                  <SurveyPreviewPage />
                </SurveyPermissionRoute>
              }
            />
            <Route
              path=":id/responses"
              element={
                <SurveyPermissionRoute permissions={SURVEY_RESPONSES_ROUTE_PERMISSIONS}>
                  <SurveyResponsesPage />
                </SurveyPermissionRoute>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <SaveProgressDialog
        open={showSaveDialog}
        onSave={handleSaveConfirm}
        onCancel={handleSaveCancel}
        onDismiss={handleSaveDismiss}
        projectName={currentProject?.projectName}
        saving={saving}
        changesTracker={changesTracker}
      />
    </>
  );
};

const AppContent = () => {
  const navigate = useNavigate();
  
  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    navigate(originalUri || '/');
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <TenantProvider>
        {/* Inside TenantProvider: AI usage is scoped to the selected client */}
        <AiUsageProvider>
          <ErrorToastProvider>
            <CustomAlertProvider>
              <ProjectProgressProvider>
                <AppContentWithDialog />
              </ProjectProgressProvider>
            </CustomAlertProvider>
          </ErrorToastProvider>
        </AiUsageProvider>
      </TenantProvider>
    </Security>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

export default App;
